import { acknowledgementCoverage } from '@qualicloud/domain';
import {
  Badge,
  Card,
  CardHeader,
  EmptyState,
  IconAlertTriangle,
  IconCheckCircle,
  IconUsers,
  MetricCard,
  Table,
  cn,
  type TableColumn,
} from '@/design-system';
import type { CircularRecipient } from '@/lib/api/sgq.types';
import { formatRegulatedDateTime } from '@/lib/format/datetime';

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * COBERTURA DE LEITURA DA CIRCULAR — sgq.md §D.3 e §E.5
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * É A EVIDÊNCIA QUE A CIRCULAR EXISTE PARA PRODUZIR. Sem esta matriz, "a
 * equipe foi comunicada" é afirmação sem lastro.
 *
 * O percentual e o número de pendentes vêm de `acknowledgementCoverage`
 * (@qualicloud/domain) — a mesma função que o CORE usa para preencher
 * `coveragePercent` na listagem. Recalcular com outra fórmula aqui produziria
 * dois números para o mesmo fato, que é exatamente o gênero de defeito que o
 * legado tinha nos indicadores.
 *
 * O QUE MUDA EM RELAÇÃO AO LEGADO
 *  · a coluna "Ciência em" mostra a PRIMEIRA ciência, que é imutável. O legado
 *    fazia `UPDATE ... SET lido_em = NOW()` sem `WHERE lido_em IS NULL`, então
 *    cada reabertura sobrescrevia o carimbo e o sistema guardava a ÚLTIMA
 *    leitura (BUG-CIR-01) — o oposto do que serve como evidência;
 *  · o hostname aparece marcado como DECLARADO, ao lado do IP OBSERVADO pelo
 *    servidor. No legado o hostname vinha de `gethostbyaddr` do IP do cliente e
 *    era exibido como se fosse fato verificado (D-CIRC-02).
 *
 * ACESSIBILIDADE: a barra de cobertura é `role="progressbar"` com
 * `aria-valuetext` em português ("62,5% — 5 de 8 destinatários"), porque
 * `aria-valuenow` sozinho faz o leitor de tela anunciar "62" sem unidade.
 */

export interface AcknowledgementCoveragePanelProps {
  readonly recipients: readonly CircularRecipient[];
  readonly timeZone: string;
  /** Percentual que o servidor calculou. Serve de conferência ao valor local. */
  readonly serverCoveragePercent?: number;
  readonly className?: string;
}

export function AcknowledgementCoveragePanel({
  recipients,
  timeZone,
  serverCoveragePercent,
  className,
}: AcknowledgementCoveragePanelProps) {
  const acknowledgedCount = recipients.filter((item) => item.acknowledgedAt !== null).length;
  const { pending, percentage } = acknowledgementCoverage({
    recipientCount: recipients.length,
    acknowledgedCount,
  });

  const columns: Array<TableColumn<CircularRecipient>> = [
    {
      id: 'user',
      header: 'Destinatário',
      width: '28%',
      cell: (row) => (
        <span className="font-medium text-content">{row.userName ?? 'Colaborador removido'}</span>
      ),
    },
    {
      id: 'status',
      header: 'Ciência',
      width: '14%',
      cell: (row) =>
        row.acknowledgedAt ? (
          <Badge tone="success" srPrefix="Situação:" icon={<IconCheckCircle size={11} />}>
            Registrada
          </Badge>
        ) : (
          <Badge tone="warning" srPrefix="Situação:" icon={<IconAlertTriangle size={11} />}>
            Pendente
          </Badge>
        ),
    },
    {
      id: 'acknowledgedAt',
      header: 'Primeira ciência',
      width: '22%',
      cell: (row) => (
        <span className="qc-tabular text-content-muted">
          {row.acknowledgedAt ? formatRegulatedDateTime(row.acknowledgedAt, timeZone) : '—'}
        </span>
      ),
    },
    {
      id: 'dueAt',
      header: 'Prazo',
      width: '14%',
      cell: (row) => (
        <span className="qc-tabular text-content-muted">
          {row.dueAt ? formatRegulatedDateTime(row.dueAt, timeZone) : '—'}
        </span>
      ),
    },
    {
      id: 'origin',
      header: 'Origem do registro',
      width: '22%',
      cell: (row) =>
        row.acknowledgedAt ? (
          <span className="text-xs text-content-muted">
            <span className="block">
              IP observado: <span className="font-mono">{row.ipAddress ?? '—'}</span>
            </span>
            <span className="block">
              Host declarado: <span className="font-mono">{row.declaredHostname ?? '—'}</span>
            </span>
            <span className="qc-sr-only">
              O endereço IP é observado pelo servidor; o nome de host é declarado pelo cliente e não
              é verificado.
            </span>
          </span>
        ) : (
          <span className="text-content-subtle">—</span>
        ),
    },
  ];

  return (
    <Card padding="none" className={cn('min-w-0', className)}>
      <div className="p-4">
        <CardHeader
          headingLevel={3}
          title="Cobertura de leitura"
          description="Quem tomou ciência, quando e a partir de onde. É a evidência de comunicação exigida pelo sistema da qualidade."
        />

        <div className="mt-4 grid gap-3 sm:grid-cols-3">
          <MetricCard
            label="Destinatários"
            value={recipients.length}
            icon={<IconUsers size={16} />}
            hint="Quem precisa registrar ciência desta circular."
          />
          <MetricCard
            label="Com ciência"
            value={acknowledgedCount}
            tone="success"
            icon={<IconCheckCircle size={16} />}
            hint="Ciência registrada, com data e origem."
          />
          {/*
            O ícone acompanha o tom porque a borda colorida é o único sinal
            visual do MetricCard: com zero pendente, um triângulo de alerta
            fixo diria o contrário do número que está logo abaixo dele.
          */}
          <MetricCard
            label="Sem ciência"
            value={pending}
            tone={pending > 0 ? 'warning' : 'neutral'}
            icon={pending > 0 ? <IconAlertTriangle size={16} /> : <IconCheckCircle size={16} />}
            hint={
              pending > 0
                ? 'Ainda precisam registrar ciência.'
                : 'Todos os destinatários registraram ciência.'
            }
          />
        </div>

        <div className="mt-4">
          <div className="flex items-baseline justify-between gap-3">
            <p className="text-sm font-medium text-content">Percentual de leitura</p>
            <p className="text-sm font-semibold text-content qc-tabular">
              {formatPercent(percentage)}
            </p>
          </div>
          <div
            role="progressbar"
            aria-valuenow={Math.round(percentage)}
            aria-valuemin={0}
            aria-valuemax={100}
            aria-valuetext={`${formatPercent(percentage)} — ${acknowledgedCount} de ${
              recipients.length
            } ${recipients.length === 1 ? 'destinatário' : 'destinatários'} com ciência registrada`}
            aria-label="Cobertura de leitura da circular"
            className="mt-2 h-2.5 w-full overflow-hidden rounded-full bg-surface-sunken"
          >
            <div
              className={cn(
                'h-full rounded-full',
                percentage >= 100 ? 'bg-success' : percentage >= 50 ? 'bg-warning' : 'bg-danger',
              )}
              style={{ width: `${Math.min(100, Math.max(0, percentage))}%` }}
            />
          </div>

          {/*
            Divergência entre o percentual local e o do servidor significa que a
            lista exibida está incompleta (paginação, filtro de visibilidade).
            Dizer isso é melhor do que mostrar dois números sem explicação.
          */}
          {typeof serverCoveragePercent === 'number' &&
          Math.abs(serverCoveragePercent - percentage) > 0.05 ? (
            <p role="status" className="mt-2 text-xs text-content-muted">
              O servidor calculou {formatPercent(serverCoveragePercent)} para esta circular. A
              diferença indica que a lista abaixo não traz todos os destinatários visíveis para
              você.
            </p>
          ) : null}
        </div>
      </div>

      {recipients.length === 0 ? (
        <EmptyState
          variant="compact"
          icon={<IconUsers size={20} />}
          title="Nenhum destinatário"
          description="Esta circular ainda não tem destinatários. Sem destinatário não há ciência a registrar, e a circular não produz evidência de comunicação."
        />
      ) : (
        <div className="border-t border-border">
          <Table<CircularRecipient>
            caption="Matriz de leitura da circular: destinatário, situação da ciência, data da primeira ciência, prazo e origem do registro."
            columns={columns}
            rows={[...recipients]}
            rowKey={(row) => row.id}
            density="comfortable"
            className="rounded-none border-0"
          />
        </div>
      )}
    </Card>
  );
}

function formatPercent(value: number): string {
  return `${value.toFixed(1).replace('.', ',')}%`;
}
